import Link from 'next/link'
import { Icon } from '@/components/ui/Icon'
import { Wordmark } from '@/components/workspace/Logo'
import { SIGNAL_CATEGORIES, SIGNAL_META } from '@/lib/signals'
import { btn, card, cx } from '@/lib/ui'
import { AUDIENCES, FEATURES, FOOTER, HERO, PILLARS, STEPS, faq } from './content'
import { Screenshot } from './Screenshot'
import { StickyBar } from './StickyBar'

/**
 * The front door for someone who is not signed in.
 *
 * A server component; the only client piece is the sticky bar that appears
 * once the wordmark has scrolled away. The FAQ is native <details>, and the
 * only motion is the button hover the whole app already has. It reuses the workspace's surfaces (panel, glass, pills)
 * rather than inventing a marketing look, so the page and the product
 * behind it are recognisably the same thing.
 */
export function Landing({ signupsOpen }: { signupsOpen: boolean }) {
  const year = new Date().getFullYear()
  return (
    <div className="flex min-h-dvh flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-full focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:shadow-[var(--shadow-float)]"
      >
        Skip to content
      </a>

      {/* No header of its own: the page opens on the wordmark, and the mark
          with the sign-in buttons only appears once that has scrolled away. */}
      <StickyBar signupsOpen={signupsOpen} watch="hero-wordmark" />

      <main id="main" className="flex-1">
        {/* Hero */}
        <section aria-labelledby="hero-title" className={cx(wrap, 'pt-4 sm:pt-6')}>
          <div className="gradient-hero rounded-[var(--radius-panel)] px-6 pb-8 pt-10 sm:px-12 sm:pt-14">
            <div className="mx-auto max-w-3xl text-center">
              <div id="hero-wordmark" className="flex justify-center">
                <Wordmark className="h-10 w-auto text-[var(--ink)] sm:h-14" />
              </div>
              <h1 id="hero-title" className="mt-8 text-4xl font-semibold tracking-tight sm:mt-10 sm:text-5xl">
                {HERO.title}
              </h1>
              <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">
                {HERO.intro}
              </p>
              <div className="mt-7 flex flex-wrap items-center justify-center gap-2.5">
                {signupsOpen && (
                  <Link href="/register" className={cx(btn.base, btn.primary)}>
                    Create an account
                    <Icon name="arrowRight" className="h-4 w-4" />
                  </Link>
                )}
                <Link href="/login" className={cx(btn.base, btn.secondary)}>
                  Sign in
                </Link>
              </div>
              <p className="mt-3 text-xs text-muted">{HERO.note}</p>
            </div>
            <div className="mx-auto mt-10 max-w-5xl">
              <Screenshot shot={HERO.shot} priority sizes="(min-width: 1024px) 1024px, 100vw" />
              <ul aria-label="Cable colours" className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-1.5">
                {SIGNAL_CATEGORIES.map((signal) => (
                  <li key={signal} className="flex items-center gap-1.5 text-[11px] text-muted">
                    <span
                      aria-hidden="true"
                      className="h-2 w-2 rounded-full"
                      style={{ background: SIGNAL_META[signal].color }}
                    />
                    {SIGNAL_META[signal].label}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* What it does */}
        <section aria-labelledby="pillars-title" className={cx(wrap, 'pt-16 sm:pt-24')}>
          <h2 id="pillars-title" className={h2}>
            What ROUTR does
          </h2>
          <ul className="mt-8 grid gap-4 md:grid-cols-3">
            {PILLARS.map((item) => (
              <li key={item.title} className={cx(card, 'p-6')}>
                <IconTile name={item.icon} />
                <h3 className="mt-4 text-base font-semibold tracking-tight">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted">{item.body}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* How it works */}
        <section aria-labelledby="steps-title" className={cx(wrap, 'pt-16 sm:pt-24')}>
          <h2 id="steps-title" className={h2}>
            How it works
          </h2>
          <ol className="mt-8 space-y-12 sm:space-y-16">
            {STEPS.map((step, index) => (
              <li
                key={step.title}
                className={cx(
                  'grid items-center gap-6 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:gap-10',
                  index % 2 === 1 && 'md:[&>*:first-child]:order-last',
                )}
              >
                <div>
                  <span className="font-mono text-xs text-muted">Step {index + 1}</span>
                  <h3 className="mt-1.5 text-xl font-semibold tracking-tight sm:text-2xl">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted sm:text-base">{step.body}</p>
                </div>
                <div className={cx('grid gap-4', step.shots.length > 1 && 'sm:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] sm:items-start')}>
                  {step.shots.map((shot) => (
                    <Screenshot key={shot.alt} shot={shot} />
                  ))}
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Who it's for */}
        <section aria-labelledby="audience-title" className={cx(wrap, 'pt-16 sm:pt-24')}>
          <h2 id="audience-title" className={h2}>
            Built for
          </h2>
          <ul className="mt-8 grid gap-4 md:grid-cols-3">
            {AUDIENCES.map((item) => (
              <li key={item.title} className={cx('glass rounded-[var(--radius-card)] p-6')}>
                <IconTile name={item.icon} />
                <h3 className="mt-4 text-base font-semibold tracking-tight">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted">{item.body}</p>
              </li>
            ))}
          </ul>
        </section>

        {/* Feature grid */}
        <section aria-labelledby="features-title" className={cx(wrap, 'pt-16 sm:pt-24')}>
          <h2 id="features-title" className={h2}>
            What you get
          </h2>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((item) => (
              <li key={item.title} className={cx(card, 'flex gap-4 p-5')}>
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[var(--surface-raised)] text-foreground">
                  <Icon name={item.icon} className="h-4 w-4" />
                </span>
                <div>
                  <h3 className="text-sm font-semibold">{item.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted">{item.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ */}
        <section aria-labelledby="faq-title" className={cx(wrap, 'pt-16 sm:pt-24')}>
          <h2 id="faq-title" className={h2}>
            Questions
          </h2>
          <div className={cx(card, 'mt-8 divide-y divide-[var(--border)] px-6')}>
            {faq(signupsOpen).map((item) => (
              <details key={item.q} className="group py-4">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-base font-medium">
                  {item.q}
                  <Icon
                    name="chevronDown"
                    className="h-4 w-4 shrink-0 text-muted motion-safe:transition-transform group-open:rotate-180"
                  />
                </summary>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Closing call */}
        <section aria-labelledby="cta-title" className={cx(wrap, 'pt-16 sm:pt-24')}>
          <div className="gradient-pastel rounded-[var(--radius-panel)] px-6 py-12 text-center sm:px-12">
            <h2 id="cta-title" className="text-2xl font-semibold tracking-tight sm:text-3xl">
              Lay out the next show in an afternoon.
            </h2>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
              {signupsOpen && (
                <Link href="/register" className={cx(btn.base, btn.primary)}>
                  Create an account
                </Link>
              )}
              <Link href="/login" className={cx(btn.base, btn.secondary)}>
                Sign in
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className={cx(wrap, 'flex flex-col gap-4 py-10 sm:flex-row sm:items-center sm:justify-between sm:py-12')}>
        <div className="flex items-center gap-4">
          <Wordmark className="h-6 w-auto text-[var(--ink)]" />
          <p className="text-xs text-muted">{FOOTER.credit}</p>
        </div>
        <nav aria-label="Footer" className="flex items-center gap-4 text-xs text-muted">
          <Link href="/login" className="hover:text-foreground">
            Sign in
          </Link>
          {signupsOpen && (
            <Link href="/register" className="hover:text-foreground">
              Create an account
            </Link>
          )}
          <span>© {year} Mfalme Studio</span>
        </nav>
      </footer>
    </div>
  )
}

const wrap = 'mx-auto w-full max-w-6xl px-5 sm:px-8'
const h2 = 'text-2xl font-semibold tracking-tight sm:text-3xl'

function IconTile({ name }: { name: Parameters<typeof Icon>[0]['name'] }) {
  return (
    <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--ink)] text-on-ink shadow-[var(--shadow-btn)]">
      <Icon name={name} className="h-[18px] w-[18px]" />
    </span>
  )
}
